import type { ReleaseId } from "./releases";
/**
 * Flower species — the plantable catalogue for the horticulture sim.
 *
 * 109 species (84 real + 16 fantasy on the level ladder, 9 premium — see level.ts), each drawn by its
 * bespoke renderer in render/plants/<id>.ts, which reads this data at any growth stage. A plant
 * grows continuously from progress 0 (just-sown seed) to 1 (fully matured); the
 * named STAGES are thresholds the renderer/visuals key off, but growth is smooth.
 *
 * Economy: buy `seedPrice` to sow, sell a matured plant for `sellValue`. Coins.
 *
 * THE PALETTE (STYLE_GUIDE §2.2) — read this before changing any colour below.
 *
 * Every `leaf`/`leafDark`/`stem`/`petal`/`petalDark`/`center`/`leafAccent` on this file is the colour a
 * renderer is HANDED before it draws a single shape, so no plant can be on-style if its entry here is not.
 * The whole roster was moved onto the guide's families in one pass; `daisy` and `marigold` were moved by
 * hand first and are the CALIBRATION every other species was matched to. The shape of that move:
 *
 *   foliage   rotated toward the fish sheet's BLUE-green and taken down in value and saturation. Roster
 *             mean leaf hue 121° → 128°, mean lightness 43 → 35. A leaf that would read as "grass on a
 *             sunny day" is too warm; seven species were on a yellow-green under hue 90 and one remains
 *             (lightbloom, deliberately — see below).
 *   highlight NEVER `0xffffff`, which §2.2 forbids outright — the brightest thing in any reference is an
 *             off-white. There are TWO off-whites and picking the wrong one is the easy mistake: a warm
 *             ivory (~hue 45) for ordinary petals, and a COOL off-white (~hue 150-195) for anything whose
 *             identity is cold light, because the fish sheet's brightest note is cool. Rotating a
 *             frostfern or a crystalbloom to ivory would warm the exact plants that must stay cold.
 *   pale tint a light colour still carrying its bloom's own hue (a foxglove centre, an azalea blush) KEEPS
 *             that hue and loses roughly half its saturation. Flattening these to ivory instead would have
 *             given eleven species one identical centre, which is why they are their own family.
 *   accents   warm accents stay genuinely warm and stay SATURATED — they only go deeper. Desaturating an
 *             orange is what turns it muddy brown, so value came down and chroma largely did not.
 *
 * Identity is the constraint the whole pass is held to: a poppy still reads red, a lavender purple, a
 * marigold orange. It is a shift of temperature and value, not a repaint. Species whose foliage is
 * deliberately NOT a green — `lightbloom`'s gold, `emberbloom`/`phoenixLily`/`magmaSucculent`'s scorched
 * browns, `voidOrchid`'s near-black — were kept out of the green rotation on purpose; do not "fix" them.
 */

/** A plant species id. Was a fixed union of 5; widened to string so the
 *  catalogue can grow to 100 without editing a type union. Ids are still the
 *  object keys of FLOWERS and remain stable save keys. */
export type FlowerId = string;

/** Growth stages a plant passes through (progress thresholds, inclusive lower). */
export type GrowthStage = "seed" | "seedling" | "sprout" | "juvenile" | "mature";

const STAGE_THRESHOLDS: Array<{ stage: GrowthStage; at: number }> = [
  { stage: "seed", at: 0.0 },
  { stage: "seedling", at: 0.08 },
  { stage: "sprout", at: 0.3 },
  { stage: "juvenile", at: 0.6 },
  { stage: "mature", at: 0.95 },
];

export function stageFor(progress: number): GrowthStage {
  let s: GrowthStage = "seed";
  for (const t of STAGE_THRESHOLDS) if (progress >= t.at) s = t.stage;
  return s;
}

/**
 * The overall GROWTH FORM of a plant — selects which procedural renderer draws
 * it. This is the backbone of catalogue variety (succulents vs vines vs grasses
 * read completely differently, not just recoloured bushes).
 */
export type PlantForm =
  | "bush" // leafy crown + flower stalks (rose, daisy, classic flowers)
  | "rosette" // succulent: fleshy leaves radiating from a centre (echeveria, aloe)
  | "columnar" // upright fleshy stems / pads / cylinders (cactus, jade, snake plant)
  | "trailing" // ivy / vines spilling over the pot rim (pothos, ivy, string-of-pearls)
  | "grass" // upright blades / reeds fanning from the base (fountain grass)
  | "fern" // arching fronds with pinnae (boston fern, maidenhair)
  | "shrub"; // dense rounded woody mound, small leaves (boxwood, azalea)

/** How a species' blossoms are arranged — drives the procedural bloom drawing. */
export type BloomForm =
  | "rosette" // tightly layered concentric petals (rose)
  | "cup" // a few broad petals forming a cup (tulip)
  | "ray" // flat ring of narrow ray petals around a disc (daisy)
  | "disc" // big seed disc ringed by short petals on a tall stalk (sunflower)
  | "spike" // many tiny florets up a vertical spike (lavender)
  | "bell" // hanging bell/trumpet blossoms (foxglove, bluebell)
  | "cluster" // a rounded umbel/pom of small florets (allium, hydrangea)
  | "star" // 5-point flat star flowers (succulent blooms, periwinkle)
  | "pom" // dense rounded ball of many petals (marigold, chrysanthemum)
  | "berry" // small round fruits instead of petals (holly, pepper)
  | "trumpet" // ring of petals around a raised central cup/corona (daffodil)
  | "orb" // FANTASY: a glowing luminous sphere with a halo + sparks
  | "crystal" // FANTASY: faceted crystalline spire/gem blossom
  | "lantern"; // FANTASY: a hanging glowing paper-lantern bloom

/** Leaf silhouette for the foliage — gives each species a different green look. */
export type LeafShape =
  | "blade" // long narrow strap leaves (tulip, lavender)
  | "oval" // broad rounded leaves (rose, sunflower, daisy)
  | "lobed" // toothed/serrated leaves
  | "needle" // fine needles (rosemary, conifers)
  | "paddle" // flat fleshy pads (prickly pear, jade)
  | "heart" // heart-shaped (pothos, philodendron, ivy)
  | "frond" // pinnate fern frond
  | "round" // round fleshy succulent leaf
  | "scalloped"; // wavy-edged rounded leaf

export interface FlowerSpecies {
  id: FlowerId;
  name: string;
  /** the content release this belongs to (sim/releases.ts) — hidden and never granted until that release is on.
   *  Absent (everything today) = always available. */
  release?: ReleaseId;
  /** A compact emoji used in shop/inventory chips. */
  icon: string;
  seedPrice: number;
  sellValue: number;
  /** LEGACY explicit grow time. No longer used for growth — base time is now DERIVED
   *  from sellValue via baseGrowSeconds(). Retained on existing data; ignored. */
  growSeconds: number;
  /** Optional explicit base grow seconds, overriding the rarity-derived curve for a
   *  special-case plant. Leave unset for the normal value-scaled time. */
  growSecondsOverride?: number;

  /** Overall growth form → which renderer draws it. Defaults to "bush". */
  form?: PlantForm;
  /** Whether this plant produces blooms at maturity. Defaults to true for bush,
   *  false for purely-foliage forms unless set. Non-flowering plants skip all
   *  bloom drawing and use foliage-only colours. */
  flowering?: boolean;

  /** Leaf/stem greens. */
  leaf: number;
  leafDark: number;
  stem: number;
  /** Blossom colours (petal + a deeper shade + the flower centre). */
  petal: number;
  petalDark: number;
  center: number;
  bloom: BloomForm;
  /** Petals per blossom (meaning depends on bloom form). */
  petals: number;

  /** A mature plant = a BUSH of foliage with a CLUSTER of blooms on/above it
   *  (matches the reference art: leafy mound + several attractive flowers). */
  /** How many blooms a mature plant shows (random in [min,max] per plant). */
  bloomsMin: number;
  bloomsMax: number;
  /** Mature plant height (foliage + blooms) in POT-LOCAL px above the soil. */
  matureHeight: number;
  /** Mature half-width of the foliage bush in pot-local px. */
  bushWidth: number;
  /** Foliage leaf count at maturity (bushiness). */
  leafCount: number;
  /** Mature single-bloom radius in pot-local px. */
  bloomRadius: number;
  /** How tall the flower STALKS (peduncles) rise above the foliage crown, as a
   *  fraction of matureHeight. Tall = blooms held high (sunflower/lavender/tulip);
   *  short = blooms sit just above/among the leaves (daisy/rose). Every bloom is
   *  always borne on a real stalk that grows from the crown — never floating. */
  stalkFrac: number;
  /** Foliage leaf silhouette. */
  leafShape: LeafShape;

  // ---- form-specific (all optional; renderers fall back to sensible defaults) ----
  /** rosette: number of fleshy leaves in the rosette. */
  rosetteLeaves?: number;
  /** columnar: number of stems / pads / cylinders. */
  stemCount?: number;
  /** columnar: 0 = slim cylinder cactus … 1 = round paddle/pad. */
  padness?: number;
  /** columnar/cactus: draw areole spines. */
  spines?: boolean;
  /** trailing: how far vines spill below the pot rim (pot-local px). */
  trailLength?: number;
  /** trailing/grass/fern: number of strands / blades / fronds. */
  strandCount?: number;
  /** grass: plume seed-head at the blade tips. */
  plume?: boolean;
  /** shrub/holly: show berries. */
  berries?: boolean;
  /** A second accent leaf colour for variegation (edges / stripes). */
  leafAccent?: number;

  /** PREFERRED grow-light colour (a `LightColor.id`: "white" | "blue" | "red" | "magenta" | "uv"). When a
   *  plant is grown under a light of ITS preferred colour AT NIGHT, it grows up to +55% faster (tier-scaled).
   *  Any colour still grants its universal harvest effect regardless of preference. Split ~evenly across the
   *  four common colours; very few plants (mostly fantasy) prefer blacklight. Default (unset) = "white". */
  preferredLight?: string;
}

export const FLOWERS: Record<FlowerId, FlowerSpecies> = {
  rose: {
    id: "rose", preferredLight: "red", name: "Rose", icon: "🌹", form: "bush",
    seedPrice: 58, sellValue: 152, growSeconds: 8551,
    leaf: 0x2e5b35, leafDark: 0x1d3d23, stem: 0x325a39,
    petal: 0xd62848, petalDark: 0x94223b, center: 0x691427,
    bloom: "rosette", petals: 12,
    bloomsMin: 3, bloomsMax: 5, matureHeight: 124, bushWidth: 46,
    leafCount: 16, bloomRadius: 19, stalkFrac: 0.46, leafShape: "lobed",
  },
  tulip: {
    id: "tulip", preferredLight: "magenta", name: "Tulip", icon: "🌷",
    seedPrice: 52, sellValue: 136, growSeconds: 8551,
    leaf: 0x396842, leafDark: 0x254d2c, stem: 0x396842,
    petal: 0xe75084, petalDark: 0xc23c72, center: 0xe3d295,
    bloom: "cup", petals: 6,
    bloomsMin: 3, bloomsMax: 5, matureHeight: 140, bushWidth: 40,
    leafCount: 9, bloomRadius: 16, stalkFrac: 0.6, leafShape: "blade",
  },
  daisy: {
    id: "daisy", preferredLight: "white", name: "Daisy", icon: "🌼",
    seedPrice: 15, sellValue: 40, growSeconds: 5052,
    // STYLE_GUIDE palette: cold dark leaf greens, IVORY petals (never white), an ochre centre.
    leaf: 0x3f6e48, leafDark: 0x27462f, stem: 0x4a7a4c,
    petal: 0xece4cc, petalDark: 0xc9bea0, center: 0xd8962e,
    bloom: "ray", petals: 13,
    bloomsMin: 4, bloomsMax: 7, matureHeight: 116, bushWidth: 48,
    leafCount: 16, bloomRadius: 15, stalkFrac: 0.54, leafShape: "lobed",
  },
  sunflower: {
    id: "sunflower", preferredLight: "red", name: "Sunflower", icon: "🌻",
    seedPrice: 56, sellValue: 148, growSeconds: 5052,
    leaf: 0x37653c, leafDark: 0x26492a, stem: 0x396839,
    petal: 0xe19c12, petalDark: 0xc27918, center: 0x4c311c,
    bloom: "disc", petals: 26,
    bloomsMin: 1, bloomsMax: 3, matureHeight: 170, bushWidth: 44,
    leafCount: 11, bloomRadius: 26, stalkFrac: 0.66, leafShape: "oval",
  },
  lavender: {
    id: "lavender", preferredLight: "magenta", name: "Lavender", icon: "💜",
    seedPrice: 49, sellValue: 130, growSeconds: 7877,
    leaf: 0x506c4d, leafDark: 0x3f5540, stem: 0x506c4d,
    petal: 0x8557be, petalDark: 0x664791, center: 0x523976,
    bloom: "spike", petals: 22,
    bloomsMin: 5, bloomsMax: 8, matureHeight: 132, bushWidth: 40,
    leafCount: 12, bloomRadius: 10, stalkFrac: 0.62, leafShape: "blade",
  },

  // ---- Batch 1: SUCCULENTS ----------------------------------------------
  // Rosette succulents (fleshy leaves radiating from a centre).
  echeveria: {
    id: "echeveria", preferredLight: "blue", name: "Echeveria", icon: "🪷", form: "rosette", flowering: true,
    seedPrice: 30, sellValue: 80, growSeconds: 10598,
    leaf: 0x6e9178, leafDark: 0x506959, stem: 0x65856d, leafAccent: 0xd36c4c,
    petal: 0xe78b22, petalDark: 0xbb6f22, center: 0xf7b749,
    bloom: "bell", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 96, bushWidth: 58, leafCount: 22, bloomRadius: 9,
    stalkFrac: 0.85, leafShape: "round", rosetteLeaves: 22,
  },
  aloe: {
    id: "aloe", preferredLight: "blue", name: "Aloe Vera", icon: "🌵", form: "rosette", flowering: false,
    seedPrice: 27, sellValue: 72, growSeconds: 12645,
    leaf: 0x58815c, leafDark: 0x405e44, stem: 0x58815c, leafAccent: 0xb4c39a,
    petal: 0xda513e, petalDark: 0xa3433a, center: 0xe0af89,
    bloom: "spike", petals: 14, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 120, bushWidth: 54, leafCount: 12, bloomRadius: 9,
    stalkFrac: 0.8, leafShape: "blade", rosetteLeaves: 11,
  },
  henChicks: {
    id: "henChicks", preferredLight: "white", name: "Hens & Chicks", icon: "🌿", form: "rosette", flowering: false,
    seedPrice: 16, sellValue: 42, growSeconds: 9146,
    leaf: 0x628b56, leafDark: 0x476640, stem: 0x628b56, leafAccent: 0xa44d62,
    petal: 0xb76586, petalDark: 0x8d516d, center: 0xd3b78c,
    bloom: "star", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 60, bushWidth: 56, leafCount: 26, bloomRadius: 7,
    stalkFrac: 0.7, leafShape: "round", rosetteLeaves: 26,
  },
  haworthia: {
    id: "haworthia", preferredLight: "white", name: "Zebra Haworthia", icon: "🦓", form: "rosette", flowering: false,
    seedPrice: 23, sellValue: 60, growSeconds: 12050,
    leaf: 0x38533f, leafDark: 0x213526, stem: 0x38533f, leafAccent: 0xc1d0b0,
    petal: 0xe4ddca, petalDark: 0xbec6ac, center: 0xdeb548,
    bloom: "star", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 74, bushWidth: 44, leafCount: 16, bloomRadius: 6,
    stalkFrac: 0.8, leafShape: "blade", rosetteLeaves: 16,
  },
  agave: {
    id: "agave", preferredLight: "blue", name: "Blue Agave", icon: "🌵", form: "rosette", flowering: false,
    seedPrice: 34, sellValue: 90, growSeconds: 16548,
    leaf: 0x7299a3, leafDark: 0x4f7680, stem: 0x7299a3, leafAccent: 0xa3883a,
    petal: 0xdcbe49, petalDark: 0xae963f, center: 0xe1d18e,
    bloom: "spike", petals: 16, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 112, bushWidth: 60, leafCount: 14, bloomRadius: 8,
    stalkFrac: 0.85, leafShape: "blade", rosetteLeaves: 13,
  },
  sempervivum: {
    id: "sempervivum", preferredLight: "white", name: "Cobweb Houseleek", icon: "🕸️", form: "rosette", flowering: false,
    seedPrice: 19, sellValue: 50, growSeconds: 9146,
    leaf: 0x59734e, leafDark: 0x3c5234, stem: 0x59734e, leafAccent: 0xcc363b,
    petal: 0xd45882, petalDark: 0xa44d71, center: 0xd3b78c,
    bloom: "star", petals: 7, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 56, bushWidth: 50, leafCount: 30, bloomRadius: 6,
    stalkFrac: 0.7, leafShape: "round", rosetteLeaves: 30,
  },

  // Columnar / pad succulents.
  jade: {
    id: "jade", preferredLight: "blue", name: "Jade Plant", icon: "🪙", form: "columnar", flowering: false,
    seedPrice: 21, sellValue: 56, growSeconds: 12645,
    leaf: 0x466a4e, leafDark: 0x304f38, stem: 0x5e412c, leafAccent: 0xcc5336,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xe7ba50,
    bloom: "cluster", petals: 16, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 110, bushWidth: 50, leafCount: 0, bloomRadius: 8,
    stalkFrac: 0.5, leafShape: "round", stemCount: 4, padness: 0.75, spines: false,
  },
  snakePlant: {
    id: "snakePlant", preferredLight: "blue", name: "Snake Plant", icon: "🐍", form: "columnar", flowering: false,
    seedPrice: 25, sellValue: 66, growSeconds: 12050,
    leaf: 0x395d41, leafDark: 0x223f2a, stem: 0x395d41, leafAccent: 0xc1ad52,
    petal: 0xcdd2a6, petalDark: 0xb7bc85, center: 0xdeb548,
    bloom: "spike", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 150, bushWidth: 44, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.5, leafShape: "blade", stemCount: 5, padness: 0, spines: false,
  },
  barrelCactus: {
    id: "barrelCactus", preferredLight: "red", name: "Barrel Cactus", icon: "🌵", form: "columnar", flowering: true,
    seedPrice: 32, sellValue: 84, growSeconds: 18000,
    leaf: 0x426748, leafDark: 0x2b4b32, stem: 0x426748, leafAccent: 0xdcad31,
    petal: 0xe7ae22, petalDark: 0xbb8a22, center: 0xe0d289,
    bloom: "ray", petals: 14, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 90, bushWidth: 56, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 1, padness: 0.55, spines: true,
  },
  pricklyPear: {
    id: "pricklyPear", preferredLight: "red", name: "Prickly Pear", icon: "🌵", form: "columnar", flowering: true,
    seedPrice: 39, sellValue: 102, growSeconds: 14501,
    leaf: 0x49724c, leafDark: 0x325435, stem: 0x49724c, leafAccent: 0xdca331,
    petal: 0xecbe20, petalDark: 0xcb8626, center: 0xdfcb86,
    bloom: "ray", petals: 16, bloomsMin: 1, bloomsMax: 2,
    matureHeight: 130, bushWidth: 64, leafCount: 0, bloomRadius: 13,
    stalkFrac: 0.4, leafShape: "paddle", stemCount: 3, padness: 1, spines: true,
  },
  goldenCactus: {
    id: "goldenCactus", preferredLight: "uv", name: "Golden Column Cactus", icon: "🌵", form: "columnar", flowering: true,
    seedPrice: 42, sellValue: 110, growSeconds: 18000,
    leaf: 0x436c47, leafDark: 0x2e4c31, stem: 0x436c47, leafAccent: 0xe4ba51,
    petal: 0xe65c86, petalDark: 0xc24871, center: 0xe3cf95,
    bloom: "ray", petals: 18, bloomsMin: 1, bloomsMax: 3,
    matureHeight: 170, bushWidth: 46, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 3, padness: 0, spines: true,
  },
  pincushionCactus: {
    id: "pincushionCactus", preferredLight: "red", name: "Pincushion Cactus", icon: "🌵", form: "columnar", flowering: true,
    seedPrice: 18, sellValue: 48, growSeconds: 14501,
    leaf: 0x4d7051, leafDark: 0x355139, stem: 0x4d7051, leafAccent: 0xcac2a2,
    petal: 0xd74a86, petalDark: 0xac4270, center: 0xe0c389,
    bloom: "ray", petals: 12, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 64, bushWidth: 54, leafCount: 0, bloomRadius: 8,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 1, padness: 0.7, spines: true,
  },
  stringPearls: {
    id: "stringPearls", preferredLight: "white", name: "String of Pearls", icon: "🟢", form: "trailing", flowering: false,
    seedPrice: 28, sellValue: 74, growSeconds: 11002,
    leaf: 0x588b56, leafDark: 0x416641, stem: 0x4b7249, leafAccent: 0x86bd71,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 84, bushWidth: 48, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.4, leafShape: "round", stemCount: 7, padness: 0.4, spines: false, trailLength: 88,
  },
  zebraCactus: {
    id: "zebraCactus", preferredLight: "red", name: "Bunny Ear Cactus", icon: "🐰", form: "columnar", flowering: true,
    seedPrice: 35, sellValue: 92, growSeconds: 14501,
    leaf: 0x487b45, leafDark: 0x305d2f, stem: 0x487b45, leafAccent: 0xd0c38d,
    petal: 0xecb22c, petalDark: 0xc48929, center: 0xe1d18e,
    bloom: "ray", petals: 14, bloomsMin: 1, bloomsMax: 2,
    matureHeight: 116, bushWidth: 58, leafCount: 0, bloomRadius: 11,
    stalkFrac: 0.4, leafShape: "paddle", stemCount: 3, padness: 1, spines: true,
  },

  // ---- Batch 2: CLASSIC FLOWERS (bush) ----------------------------------
  marigold: {
    id: "marigold", preferredLight: "red", name: "Marigold", icon: "🟠", form: "bush",
    seedPrice: 18, sellValue: 48, growSeconds: 3600,
    // STYLE_GUIDE palette: cold dark ferny greens; petals a deeper, less neon orange with a real dark base.
    leaf: 0x36603c, leafDark: 0x213f29, stem: 0x3f6b40,
    petal: 0xe07a22, petalDark: 0xa8500e, center: 0x8f3f0a,
    bloom: "pom", petals: 20, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 100, bushWidth: 50, leafCount: 16, bloomRadius: 14,
    stalkFrac: 0.42, leafShape: "lobed",
  },
  zinnia: {
    id: "zinnia", preferredLight: "red", name: "Zinnia", icon: "🌸", form: "bush",
    seedPrice: 30, sellValue: 78, growSeconds: 3600,
    leaf: 0x416b46, leafDark: 0x2c4e31, stem: 0x416b46,
    petal: 0xdc3768, petalDark: 0xad3358, center: 0xe7af22,
    bloom: "ray", petals: 16, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 120, bushWidth: 46, leafCount: 14, bloomRadius: 17,
    stalkFrac: 0.6, leafShape: "oval",
  },
  poppy: {
    id: "poppy", preferredLight: "red", name: "Poppy", icon: "🌺", form: "bush",
    seedPrice: 42, sellValue: 110, growSeconds: 5052,
    leaf: 0x50734e, leafDark: 0x395639, stem: 0x567a52,
    petal: 0xcf2d25, petalDark: 0x9c1d16, center: 0x1c0c06,
    bloom: "cup", petals: 4, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 128, bushWidth: 42, leafCount: 12, bloomRadius: 18,
    stalkFrac: 0.66, leafShape: "lobed",
  },
  pansy: {
    id: "pansy", preferredLight: "magenta", name: "Pansy", icon: "💜", form: "bush",
    seedPrice: 22, sellValue: 58, growSeconds: 5052,
    leaf: 0x426a46, leafDark: 0x2e4c31, stem: 0x426a46,
    petal: 0x6f4a91, petalDark: 0x4c306c, center: 0xe7be22,
    bloom: "ray", petals: 5, bloomsMin: 4, bloomsMax: 6,
    matureHeight: 78, bushWidth: 52, leafCount: 16, bloomRadius: 15,
    stalkFrac: 0.4, leafShape: "scalloped",
  },
  carnation: {
    id: "carnation", preferredLight: "magenta", name: "Carnation", icon: "🌷", form: "bush",
    seedPrice: 46, sellValue: 120, growSeconds: 7099,
    leaf: 0x516c5c, leafDark: 0x375143, stem: 0x516c5c,
    petal: 0xe45d89, petalDark: 0xc24871, center: 0xde9eb0,
    bloom: "pom", petals: 24, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 122, bushWidth: 44, leafCount: 12, bloomRadius: 15,
    stalkFrac: 0.62, leafShape: "blade",
  },
  chrysanthemum: {
    id: "chrysanthemum", preferredLight: "white", name: "Chrysanthemum", icon: "🌼", form: "bush",
    seedPrice: 50, sellValue: 132, growSeconds: 7099,
    leaf: 0x395d3e, leafDark: 0x233f27, stem: 0x395d3e,
    petal: 0xdf8f16, petalDark: 0xba7016, center: 0xb05d10,
    bloom: "pom", petals: 28, bloomsMin: 4, bloomsMax: 8,
    matureHeight: 110, bushWidth: 56, leafCount: 20, bloomRadius: 16,
    stalkFrac: 0.46, leafShape: "lobed",
  },
  gerbera: {
    id: "gerbera", preferredLight: "red", name: "Gerbera Daisy", icon: "🌻", form: "bush",
    seedPrice: 49, sellValue: 128, growSeconds: 7099,
    leaf: 0x416b46, leafDark: 0x2c4e31, stem: 0x416b46,
    petal: 0xe34b19, petalDark: 0xba3918, center: 0x693214,
    bloom: "ray", petals: 22, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 124, bushWidth: 48, leafCount: 14, bloomRadius: 20,
    stalkFrac: 0.64, leafShape: "lobed",
  },
  cosmos: {
    id: "cosmos", preferredLight: "magenta", name: "Cosmos", icon: "🌸", form: "bush",
    seedPrice: 36, sellValue: 96, growSeconds: 4378,
    leaf: 0x58815c, leafDark: 0x3f6043, stem: 0x58815c,
    petal: 0xda62a0, petalDark: 0xaf4e82, center: 0xe7be22,
    bloom: "ray", petals: 8, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 146, bushWidth: 50, leafCount: 18, bloomRadius: 16,
    stalkFrac: 0.66, leafShape: "needle",
  },
  peony: {
    id: "peony", preferredLight: "magenta", name: "Peony", icon: "🌹", form: "bush",
    seedPrice: 57, sellValue: 150, growSeconds: 14501,
    leaf: 0x325a39, leafDark: 0x1d3d24, stem: 0x325a39,
    petal: 0xd492a7, petalDark: 0xc6547b, center: 0xdda6b2,
    bloom: "rosette", petals: 16, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 128, bushWidth: 56, leafCount: 18, bloomRadius: 22,
    stalkFrac: 0.5, leafShape: "lobed",
  },
  hibiscus: {
    id: "hibiscus", preferredLight: "red", name: "Hibiscus", icon: "🌺", form: "bush",
    seedPrice: 57, sellValue: 150, growSeconds: 10598,
    leaf: 0x2e5b38, leafDark: 0x1b3d23, stem: 0x325a3b,
    petal: 0xe52a48, petalDark: 0xb12b42, center: 0xe7be22,
    bloom: "cup", petals: 5, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 150, bushWidth: 54, leafCount: 18, bloomRadius: 22,
    stalkFrac: 0.5, leafShape: "lobed",
  },
  geranium: {
    id: "geranium", preferredLight: "red", name: "Geranium", icon: "🔴", form: "bush",
    seedPrice: 48, sellValue: 126, growSeconds: 7099,
    leaf: 0x416a46, leafDark: 0x2c4b30, stem: 0x416a46,
    petal: 0xd42844, petalDark: 0xa22239, center: 0xd08d9a,
    bloom: "cluster", petals: 18, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 116, bushWidth: 52, leafCount: 16, bloomRadius: 16,
    stalkFrac: 0.5, leafShape: "scalloped",
  },
  aster: {
    id: "aster", preferredLight: "magenta", name: "Aster", icon: "💟", form: "bush",
    seedPrice: 43, sellValue: 112, growSeconds: 7099,
    leaf: 0x426a4b, leafDark: 0x2c4e34, stem: 0x426a4b,
    petal: 0x7a5ec4, petalDark: 0x5d4799, center: 0xe7af22,
    bloom: "ray", petals: 20, bloomsMin: 5, bloomsMax: 9,
    matureHeight: 118, bushWidth: 54, leafCount: 18, bloomRadius: 13,
    stalkFrac: 0.56, leafShape: "blade",
  },
  petunia: {
    id: "petunia", preferredLight: "magenta", name: "Petunia", icon: "🟣", form: "bush",
    seedPrice: 33, sellValue: 86, growSeconds: 5052,
    leaf: 0x426a46, leafDark: 0x2e4c31, stem: 0x426a46,
    petal: 0x9b47a8, petalDark: 0x703179, center: 0xd3c38c,
    bloom: "cup", petals: 5, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 96, bushWidth: 54, leafCount: 16, bloomRadius: 16,
    stalkFrac: 0.44, leafShape: "oval",
  },
  blackEyedSusan: {
    id: "blackEyedSusan", preferredLight: "red", name: "Black-Eyed Susan", icon: "🌼", form: "bush",
    seedPrice: 39, sellValue: 102, growSeconds: 7099,
    leaf: 0x3a603c, leafDark: 0x254327, stem: 0x3a603c,
    petal: 0xe79e22, petalDark: 0xb9832d, center: 0x2d1c0e,
    bloom: "ray", petals: 14, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 124, bushWidth: 50, leafCount: 16, bloomRadius: 16,
    stalkFrac: 0.6, leafShape: "lobed",
  },
  ranunculus: {
    id: "ranunculus", preferredLight: "magenta", name: "Ranunculus", icon: "🌷", form: "bush",
    seedPrice: 54, sellValue: 142, growSeconds: 6179,
    leaf: 0x466a4b, leafDark: 0x2f4c33, stem: 0x466a4b,
    petal: 0xe56022, petalDark: 0xb94c22, center: 0xe78c39,
    bloom: "rosette", petals: 20, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 112, bushWidth: 48, leafCount: 16, bloomRadius: 17,
    stalkFrac: 0.56, leafShape: "lobed",
  },

  // ---- Batch 3: VINES & IVY (trailing) ----------------------------------
  englishIvy: {
    id: "englishIvy", preferredLight: "blue", name: "English Ivy", icon: "🍃", form: "trailing", flowering: false,
    seedPrice: 27, sellValue: 72, growSeconds: 11002,
    leaf: 0x395d3f, leafDark: 0x223f28, stem: 0x374f34, leafAccent: 0xb4c39a,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 96, bushWidth: 70, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "lobed", strandCount: 7, trailLength: 84,
  },
  pothos: {
    id: "pothos", preferredLight: "blue", name: "Golden Pothos", icon: "🪴", form: "trailing", flowering: false,
    seedPrice: 34, sellValue: 90, growSeconds: 10598,
    leaf: 0x427143, leafDark: 0x2e522f, stem: 0x43653b, leafAccent: 0xdbcd55,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 100, bushWidth: 72, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 7, trailLength: 90,
  },
  philodendron: {
    id: "philodendron", preferredLight: "white", name: "Heartleaf Philodendron", icon: "💚", form: "trailing", flowering: false,
    seedPrice: 40, sellValue: 104, growSeconds: 10598,
    leaf: 0x325a3a, leafDark: 0x1d3a23, stem: 0x334f31, leafAccent: 0x4f824f,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 100, bushWidth: 70, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 6, trailLength: 92,
  },
  stringHearts: {
    id: "stringHearts", preferredLight: "magenta", name: "String of Hearts", icon: "💞", form: "trailing", flowering: false,
    seedPrice: 46, sellValue: 120, growSeconds: 10598,
    leaf: 0x586d59, leafDark: 0x3d513f, stem: 0x7e6f63, leafAccent: 0xb082a7,
    petal: 0xb76586, petalDark: 0x8d516d, center: 0xd3b78c,
    bloom: "bell", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 80, bushWidth: 60, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 6, trailLength: 100,
  },
  morningGlory: {
    id: "morningGlory", preferredLight: "blue", name: "Morning Glory", icon: "🌅", form: "trailing", flowering: true,
    seedPrice: 44, sellValue: 116, growSeconds: 5052,
    leaf: 0x426a48, leafDark: 0x2c4b30, stem: 0x426742, leafAccent: 0x5a9853,
    petal: 0x5a70c4, petalDark: 0x475499, center: 0xe3cf95,
    bloom: "cup", petals: 5, bloomsMin: 3, bloomsMax: 6,
    matureHeight: 110, bushWidth: 72, leafCount: 0, bloomRadius: 15,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 6, trailLength: 80,
  },
  sweetPea: {
    id: "sweetPea", preferredLight: "white", name: "Sweet Pea", icon: "🌸", form: "trailing", flowering: true,
    seedPrice: 38, sellValue: 100, growSeconds: 6179,
    leaf: 0x4b724d, leafDark: 0x335335, stem: 0x4b764a, leafAccent: 0x609e52,
    petal: 0xda62a0, petalDark: 0xaf4e82, center: 0xd6a7bd,
    bloom: "cup", petals: 5, bloomsMin: 3, bloomsMax: 6,
    matureHeight: 116, bushWidth: 66, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "oval", strandCount: 6, trailLength: 74,
  },
  nasturtium: {
    id: "nasturtium", preferredLight: "red", name: "Nasturtium", icon: "🟧", form: "trailing", flowering: true,
    seedPrice: 21, sellValue: 54, growSeconds: 4378,
    leaf: 0x4f7754, leafDark: 0x36583b, stem: 0x4d7d4d, leafAccent: 0x5bab4d,
    petal: 0xdc5e18, petalDark: 0xba4716, center: 0xe7af22,
    bloom: "cup", petals: 5, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 96, bushWidth: 74, leafCount: 0, bloomRadius: 14,
    stalkFrac: 0.4, leafShape: "round", strandCount: 6, trailLength: 70,
  },
  ivyGeranium: {
    id: "ivyGeranium", preferredLight: "magenta", name: "Ivy Geranium", icon: "🌺", form: "trailing", flowering: true,
    seedPrice: 43, sellValue: 112, growSeconds: 7099,
    leaf: 0x416a46, leafDark: 0x2c4b30, stem: 0x416a46, leafAccent: 0x5a9853,
    petal: 0xd63664, petalDark: 0xa43154, center: 0xd79fb0,
    bloom: "cluster", petals: 16, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 104, bushWidth: 70, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "lobed", strandCount: 6, trailLength: 72,
  },
  trailingLobelia: {
    id: "trailingLobelia", preferredLight: "blue", name: "Trailing Lobelia", icon: "🔵", form: "trailing", flowering: true,
    seedPrice: 24, sellValue: 64, growSeconds: 5052,
    leaf: 0x4a6b4d, leafDark: 0x345137, stem: 0x4a6b4d, leafAccent: 0x5a9853,
    petal: 0x4065c5, petalDark: 0x324d97, center: 0xe3cf95,
    bloom: "ray", petals: 5, bloomsMin: 5, bloomsMax: 9,
    matureHeight: 84, bushWidth: 70, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "oval", strandCount: 7, trailLength: 78,
  },
  creepingJenny: {
    id: "creepingJenny", preferredLight: "white", name: "Creeping Jenny", icon: "🟡", form: "trailing", flowering: true,
    seedPrice: 17, sellValue: 44, growSeconds: 8551,
    leaf: 0x508a3c, leafDark: 0x38702c, stem: 0x437e36, leafAccent: 0x70c24f,
    petal: 0xe7be22, petalDark: 0xb9922d, center: 0xe0d289,
    bloom: "ray", petals: 5, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 70, bushWidth: 76, leafCount: 0, bloomRadius: 8,
    stalkFrac: 0.4, leafShape: "round", strandCount: 7, trailLength: 82,
  },
  wateringIvy: {
    id: "wateringIvy", preferredLight: "white", name: "Algerian Ivy", icon: "🌿", form: "trailing", flowering: false,
    seedPrice: 31, sellValue: 82, growSeconds: 11002,
    leaf: 0x3d5f43, leafDark: 0x25402b, stem: 0x374f34, leafAccent: 0xb6c99a,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 100, bushWidth: 74, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "lobed", strandCount: 7, trailLength: 88,
  },
  burrosTail: {
    id: "burrosTail", preferredLight: "blue", name: "Burro's Tail", icon: "🌵", form: "trailing", flowering: false,
    seedPrice: 47, sellValue: 124, growSeconds: 12645,
    leaf: 0x6e9172, leafDark: 0x546957, stem: 0x65856a, leafAccent: 0xb3c3a6,
    petal: 0xd45882, petalDark: 0xa44d71, center: 0xd3b78c,
    bloom: "star", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 84, bushWidth: 58, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.4, leafShape: "round", strandCount: 6, trailLength: 104,
  },

  // ---- Batch 4: GRASSES, REEDS & FERNS ----------------------------------
  // Grasses & reeds.
  fountainGrass: {
    id: "fountainGrass", preferredLight: "blue", name: "Fountain Grass", icon: "🌾", form: "grass", flowering: true,
    seedPrice: 30, sellValue: 80, growSeconds: 7099,
    leaf: 0x4d7144, leafDark: 0x34522d, stem: 0x4d7144,
    petal: 0xc2ae93, petalDark: 0xa98663, center: 0xc2ae93,
    bloom: "spike", petals: 10, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 150, bushWidth: 64, leafCount: 0, bloomRadius: 8,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 48, plume: true,
  },
  blueFescue: {
    id: "blueFescue", preferredLight: "blue", name: "Blue Fescue", icon: "🟦", form: "grass", flowering: false,
    seedPrice: 20, sellValue: 52, growSeconds: 9146,
    leaf: 0x6f98b3, leafDark: 0x52778f, stem: 0x6f98b3,
    petal: 0xc3bc9a, petalDark: 0xa99563, center: 0xc3bc9a,
    bloom: "spike", petals: 8, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 92, bushWidth: 56, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 24, plume: false,
  },
  mondoGrass: {
    id: "mondoGrass", preferredLight: "white", name: "Mondo Grass", icon: "🌱", form: "grass", flowering: false,
    seedPrice: 16, sellValue: 42, growSeconds: 11002,
    leaf: 0x304f34, leafDark: 0x1c3320, stem: 0x304f34,
    petal: 0xae80b9, petalDark: 0x8a6596, center: 0xc0afc0,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 86, bushWidth: 58, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 22, plume: false,
  },
  pampasGrass: {
    id: "pampasGrass", preferredLight: "white", name: "Pampas Grass", icon: "🌾", form: "grass", flowering: true,
    seedPrice: 40, sellValue: 106, growSeconds: 14501,
    leaf: 0x59734e, leafDark: 0x3c5334, stem: 0x59734e,
    petal: 0xd2c8a6, petalDark: 0xc3af82, center: 0xd2c8a6,
    bloom: "spike", petals: 12, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 200, bushWidth: 72, leafCount: 0, bloomRadius: 10,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 44, plume: true,
  },
  papyrus: {
    id: "papyrus", preferredLight: "blue", name: "Papyrus", icon: "🪶", form: "grass", flowering: true,
    seedPrice: 37, sellValue: 98, growSeconds: 9146,
    leaf: 0x426749, leafDark: 0x2b4b32, stem: 0x426749,
    petal: 0x496a42, petalDark: 0x324e2d, center: 0x58824f,
    bloom: "cluster", petals: 18, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 188, bushWidth: 54, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 9, plume: true,
  },
  horsetail: {
    id: "horsetail", preferredLight: "blue", name: "Horsetail Reed", icon: "🎋", form: "grass", flowering: false,
    seedPrice: 27, sellValue: 70, growSeconds: 11002,
    leaf: 0x426742, leafDark: 0x2c4b2d, stem: 0x426742, leafAccent: 0x213522,
    petal: 0xc1ad52, petalDark: 0x998948, center: 0xd0b965,
    bloom: "spike", petals: 8, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 160, bushWidth: 48, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 12, plume: false,
  },
  purpleMillet: {
    id: "purpleMillet", preferredLight: "magenta", name: "Purple Millet", icon: "🌾", form: "grass", flowering: true,
    seedPrice: 33, sellValue: 88, growSeconds: 7099,
    leaf: 0x6c5460, leafDark: 0x47333e, stem: 0x604552,
    petal: 0x87556e, petalDark: 0x60374d, center: 0xa86586,
    bloom: "spike", petals: 14, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 168, bushWidth: 58, leafCount: 0, bloomRadius: 9,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 16, plume: true,
  },
  sedge: {
    id: "sedge", preferredLight: "blue", name: "Golden Sedge", icon: "🌾", form: "grass", flowering: false,
    seedPrice: 24, sellValue: 62, growSeconds: 9146,
    leaf: 0x618046, leafDark: 0x496430, stem: 0x618046, leafAccent: 0x406537,
    petal: 0xd0b965, petalDark: 0xaa9756, center: 0xd0b965,
    bloom: "spike", petals: 8, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 120, bushWidth: 60, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 20, plume: false,
  },

  // Ferns (non-flowering, arching fronds).
  bostonFern: {
    id: "bostonFern", preferredLight: "blue", name: "Boston Fern", icon: "🌿", form: "fern", flowering: false,
    seedPrice: 24, sellValue: 64, growSeconds: 11002,
    leaf: 0x3a6542, leafDark: 0x234428, stem: 0x355a35,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 124, bushWidth: 76, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 9,
  },
  maidenhairFern: {
    id: "maidenhairFern", preferredLight: "blue", name: "Maidenhair Fern", icon: "🍀", form: "fern", flowering: false,
    seedPrice: 31, sellValue: 82, growSeconds: 10598,
    leaf: 0x4f7753, leafDark: 0x335335, stem: 0x30231d,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 110, bushWidth: 70, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 8,
  },
  asparagusFern: {
    id: "asparagusFern", preferredLight: "white", name: "Asparagus Fern", icon: "🌿", form: "fern", flowering: false,
    seedPrice: 17, sellValue: 46, growSeconds: 10598,
    leaf: 0x4d7d4d, leafDark: 0x305d2f, stem: 0x4d7d4d,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "berry", petals: 4, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 118, bushWidth: 80, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 11,
  },
  birdsNestFern: {
    id: "birdsNestFern", preferredLight: "blue", name: "Bird's Nest Fern", icon: "🪺", form: "fern", flowering: false,
    seedPrice: 28, sellValue: 74, growSeconds: 12050,
    leaf: 0x427146, leafDark: 0x2c512f, stem: 0x427146, leafAccent: 0x51954e,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 104, bushWidth: 66, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 7,
  },
  buttonFern: {
    id: "buttonFern", preferredLight: "white", name: "Button Fern", icon: "🌿", form: "fern", flowering: false,
    seedPrice: 21, sellValue: 56, growSeconds: 10598,
    leaf: 0x395d3f, leafDark: 0x223f28, stem: 0x3f3126,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 96, bushWidth: 72, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 9,
  },
  staghornFern: {
    id: "staghornFern", preferredLight: "uv", name: "Staghorn Fern", icon: "🦌", form: "fern", flowering: false,
    seedPrice: 36, sellValue: 96, growSeconds: 14501,
    leaf: 0x4f734e, leafDark: 0x345333, stem: 0x4f734e, leafAccent: 0xb4c39a,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 116, bushWidth: 78, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 6,
  },

  // ---- Batch 5: BULBS & SPIKES (bush form, strap leaves) ----------------
  daffodil: {
    id: "daffodil", preferredLight: "white", name: "Daffodil", icon: "🌼", form: "bush",
    seedPrice: 49, sellValue: 128, growSeconds: 8551,
    leaf: 0x3a6544, leafDark: 0x23442b, stem: 0x436b4a,
    petal: 0xeccf20, petalDark: 0xc0a622, center: 0xe67a18,
    bloom: "trumpet", petals: 6, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 116, bushWidth: 42, leafCount: 8, bloomRadius: 16,
    stalkFrac: 0.62, leafShape: "blade",
  },
  hyacinth: {
    id: "hyacinth", preferredLight: "magenta", name: "Hyacinth", icon: "🪻", form: "bush",
    seedPrice: 51, sellValue: 134, growSeconds: 8551,
    leaf: 0x3a6542, leafDark: 0x223f28, stem: 0x3a6542,
    petal: 0x7a5ec4, petalDark: 0x5d4799, center: 0x9783d1,
    bloom: "spike", petals: 26, bloomsMin: 2, bloomsMax: 3,
    matureHeight: 104, bushWidth: 40, leafCount: 7, bloomRadius: 14,
    stalkFrac: 0.6, leafShape: "blade",
  },
  iris: {
    id: "iris", preferredLight: "magenta", name: "Bearded Iris", icon: "🌷", form: "bush",
    seedPrice: 54, sellValue: 142, growSeconds: 14501,
    leaf: 0x516c58, leafDark: 0x37513e, stem: 0x516c58,
    petal: 0x6f4a91, petalDark: 0x4c306c, center: 0xe7af22,
    bloom: "cup", petals: 6, bloomsMin: 2, bloomsMax: 3,
    matureHeight: 138, bushWidth: 46, leafCount: 8, bloomRadius: 18,
    stalkFrac: 0.66, leafShape: "blade",
  },
  gladiolus: {
    id: "gladiolus", preferredLight: "red", name: "Gladiolus", icon: "🗡️", form: "bush",
    seedPrice: 55, sellValue: 144, growSeconds: 7099,
    leaf: 0x426748, leafDark: 0x2b4b31, stem: 0x426748,
    petal: 0xe53a76, petalDark: 0xba3461, center: 0xde9eb0,
    bloom: "cup", petals: 6, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 178, bushWidth: 42, leafCount: 7, bloomRadius: 15,
    stalkFrac: 0.72, leafShape: "blade",
  },
  allium: {
    id: "allium", preferredLight: "magenta", name: "Allium", icon: "🟣", form: "bush",
    seedPrice: 53, sellValue: 140, growSeconds: 8551,
    leaf: 0x4e7352, leafDark: 0x335335, stem: 0x4c764c,
    petal: 0x8457be, petalDark: 0x674791, center: 0xa78dc8,
    bloom: "cluster", petals: 28, bloomsMin: 1, bloomsMax: 3,
    matureHeight: 156, bushWidth: 40, leafCount: 6, bloomRadius: 18,
    stalkFrac: 0.78, leafShape: "blade",
  },
  snapdragon: {
    id: "snapdragon", preferredLight: "red", name: "Snapdragon", icon: "🐉", form: "bush",
    seedPrice: 51, sellValue: 134, growSeconds: 6179,
    leaf: 0x426a48, leafDark: 0x2c4b30, stem: 0x426a48,
    petal: 0xe54571, petalDark: 0xc03a5e, center: 0xecbe20,
    bloom: "bell", petals: 5, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 144, bushWidth: 44, leafCount: 12, bloomRadius: 12,
    stalkFrac: 0.66, leafShape: "oval",
  },
  foxglove: {
    id: "foxglove", preferredLight: "magenta", name: "Foxglove", icon: "🫐", form: "bush",
    seedPrice: 55, sellValue: 144, growSeconds: 14501,
    leaf: 0x395d3f, leafDark: 0x223f28, stem: 0x3d5b3f,
    petal: 0xb264bd, petalDark: 0x885291, center: 0xd4acca,
    bloom: "bell", petals: 5, bloomsMin: 5, bloomsMax: 9,
    matureHeight: 184, bushWidth: 46, leafCount: 12, bloomRadius: 13,
    stalkFrac: 0.7, leafShape: "lobed",
  },
  delphinium: {
    id: "delphinium", preferredLight: "blue", name: "Delphinium", icon: "🔷", form: "bush",
    seedPrice: 55, sellValue: 146, growSeconds: 8551,
    leaf: 0x426747, leafDark: 0x2b4b30, stem: 0x426747,
    petal: 0x4065c5, petalDark: 0x324d97, center: 0xa3a3e7,
    bloom: "ray", petals: 6, bloomsMin: 8, bloomsMax: 14,
    matureHeight: 190, bushWidth: 48, leafCount: 14, bloomRadius: 11,
    stalkFrac: 0.72, leafShape: "lobed",
  },
  crocus: {
    id: "crocus", preferredLight: "white", name: "Crocus", icon: "🌸", form: "bush",
    seedPrice: 25, sellValue: 66, growSeconds: 7099,
    leaf: 0x436b4a, leafDark: 0x2c4e32, stem: 0x436b4a, leafAccent: 0xc1d0b0,
    petal: 0x8457be, petalDark: 0x674791, center: 0xe69318,
    bloom: "cup", petals: 6, bloomsMin: 3, bloomsMax: 6,
    matureHeight: 58, bushWidth: 44, leafCount: 10, bloomRadius: 13,
    stalkFrac: 0.5, leafShape: "blade",
  },
  freesia: {
    id: "freesia", preferredLight: "white", name: "Freesia", icon: "🌼", form: "bush",
    seedPrice: 52, sellValue: 138, growSeconds: 7099,
    leaf: 0x466a4b, leafDark: 0x2f4c33, stem: 0x466a4b,
    petal: 0xecbe20, petalDark: 0xc1922a, center: 0xe0d289,
    bloom: "trumpet", petals: 6, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 120, bushWidth: 44, leafCount: 8, bloomRadius: 12,
    stalkFrac: 0.64, leafShape: "blade",
  },
  bluebell: {
    id: "bluebell", preferredLight: "blue", name: "Bluebell", icon: "🔔", form: "bush",
    seedPrice: 45, sellValue: 118, growSeconds: 14501,
    leaf: 0x426748, leafDark: 0x2b4b31, stem: 0x426748,
    petal: 0x4d62c5, petalDark: 0x404e98, center: 0x9fa7d1,
    bloom: "bell", petals: 5, bloomsMin: 4, bloomsMax: 8,
    matureHeight: 108, bushWidth: 42, leafCount: 8, bloomRadius: 11,
    stalkFrac: 0.66, leafShape: "blade",
  },
  anemone: {
    id: "anemone", preferredLight: "white", name: "Anemone", icon: "🌺", form: "bush",
    seedPrice: 52, sellValue: 136, growSeconds: 7099,
    leaf: 0x426a46, leafDark: 0x2c4b2f, stem: 0x426a46,
    petal: 0xd62f4b, petalDark: 0xa32a43, center: 0x1d131d,
    bloom: "ray", petals: 8, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 110, bushWidth: 46, leafCount: 14, bloomRadius: 17,
    stalkFrac: 0.62, leafShape: "lobed",
  },

  // ---- Batch 6: SHRUBS & BERRIES (dense woody mound) --------------------
  boxwood: {
    id: "boxwood", preferredLight: "blue", name: "Boxwood", icon: "🟩", form: "shrub", flowering: false,
    seedPrice: 22, sellValue: 58, growSeconds: 16548,
    leaf: 0x365c39, leafDark: 0x213e25, stem: 0x5d412c,
    petal: 0xe4ddca, petalDark: 0xc6bfad, center: 0xdeb548,
    bloom: "cluster", petals: 8, bloomsMin: 0, bloomsMax: 0,
    matureHeight: 120, bushWidth: 78, leafCount: 0, bloomRadius: 5,
    stalkFrac: 0.4, leafShape: "oval",
  },
  azalea: {
    id: "azalea", preferredLight: "magenta", name: "Azalea", icon: "🌺", form: "shrub", flowering: true,
    seedPrice: 40, sellValue: 104, growSeconds: 16548,
    leaf: 0x395d3f, leafDark: 0x223f28, stem: 0x4e3327,
    petal: 0xe54584, petalDark: 0xc03a6e, center: 0xde9eb6,
    bloom: "ray", petals: 5, bloomsMin: 7, bloomsMax: 12,
    matureHeight: 118, bushWidth: 80, leafCount: 0, bloomRadius: 13,
    stalkFrac: 0.4, leafShape: "oval",
  },
  rhododendron: {
    id: "rhododendron", preferredLight: "magenta", name: "Rhododendron", icon: "🌸", form: "shrub", flowering: true,
    seedPrice: 45, sellValue: 118, growSeconds: 18000,
    leaf: 0x325a39, leafDark: 0x1d3a23, stem: 0x4e3327,
    petal: 0xac53be, petalDark: 0x814791, center: 0xd0a7d0,
    bloom: "cluster", petals: 16, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 140, bushWidth: 84, leafCount: 0, bloomRadius: 16,
    stalkFrac: 0.4, leafShape: "oval",
  },
  hydrangeaBush: {
    id: "hydrangeaBush", preferredLight: "blue", name: "Hydrangea", icon: "💙", form: "shrub", flowering: true,
    seedPrice: 43, sellValue: 114, growSeconds: 14501,
    leaf: 0x3a6542, leafDark: 0x234428, stem: 0x355a35,
    petal: 0x5a80c4, petalDark: 0x476299, center: 0x9fafd1,
    bloom: "cluster", petals: 22, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 132, bushWidth: 86, leafCount: 0, bloomRadius: 18,
    stalkFrac: 0.4, leafShape: "lobed",
  },
  miniRoseBush: {
    id: "miniRoseBush", preferredLight: "red", name: "Mini Rose Bush", icon: "🌹", form: "shrub", flowering: true,
    seedPrice: 41, sellValue: 108, growSeconds: 9678,
    leaf: 0x2e5b35, leafDark: 0x1d3d23, stem: 0x3f3126,
    petal: 0xd62848, petalDark: 0x94223b, center: 0x691427,
    bloom: "rosette", petals: 12, bloomsMin: 5, bloomsMax: 9,
    matureHeight: 122, bushWidth: 78, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "lobed",
  },
  rosemary: {
    id: "rosemary", preferredLight: "white", name: "Rosemary", icon: "🌿", form: "shrub", flowering: true,
    seedPrice: 19, sellValue: 50, growSeconds: 11002,
    leaf: 0x405c47, leafDark: 0x2b3f2f, stem: 0x444f34,
    petal: 0x758cc3, petalDark: 0x5b6f9c, center: 0xa7b1d0,
    bloom: "star", petals: 5, bloomsMin: 6, bloomsMax: 12,
    matureHeight: 130, bushWidth: 70, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.4, leafShape: "needle",
  },
  lavenderShrub: {
    id: "lavenderShrub", preferredLight: "magenta", name: "French Lavender", icon: "💜", form: "shrub", flowering: true,
    seedPrice: 26, sellValue: 68, growSeconds: 7877,
    leaf: 0x617862, leafDark: 0x485949, stem: 0x4e5d4b,
    petal: 0x8557be, petalDark: 0x664791, center: 0x523976,
    bloom: "spike", petals: 16, bloomsMin: 6, bloomsMax: 11,
    matureHeight: 124, bushWidth: 72, leafCount: 0, bloomRadius: 9,
    stalkFrac: 0.4, leafShape: "needle",
  },
  holly: {
    id: "holly", preferredLight: "red", name: "Holly", icon: "🎄", form: "shrub", flowering: false,
    seedPrice: 36, sellValue: 94, growSeconds: 18000, berries: true,
    leaf: 0x204c2a, leafDark: 0x102d18, stem: 0x4e3327, leafAccent: 0x3a6542,
    petal: 0xca2333, petalDark: 0x9c1520, center: 0x680d15,
    bloom: "berry", petals: 4, bloomsMin: 6, bloomsMax: 11,
    matureHeight: 134, bushWidth: 80, leafCount: 0, bloomRadius: 8,
    stalkFrac: 0.4, leafShape: "lobed",
  },
  winterberry: {
    id: "winterberry", preferredLight: "red", name: "Winterberry", icon: "🔴", form: "shrub", flowering: false,
    seedPrice: 33, sellValue: 88, growSeconds: 16548, berries: true,
    leaf: 0x3a603c, leafDark: 0x254327, stem: 0x5e412f,
    petal: 0xd1262b, petalDark: 0x9d1c20, center: 0x680d10,
    bloom: "berry", petals: 4, bloomsMin: 8, bloomsMax: 14,
    matureHeight: 126, bushWidth: 78, leafCount: 0, bloomRadius: 7,
    stalkFrac: 0.4, leafShape: "oval",
  },
  pepperPlant: {
    id: "pepperPlant", preferredLight: "red", name: "Ornamental Pepper", icon: "🌶️", form: "shrub", flowering: false,
    seedPrice: 29, sellValue: 76, growSeconds: 7099, berries: true,
    leaf: 0x365c39, leafDark: 0x213e25, stem: 0x355a35,
    petal: 0xdc4918, petalDark: 0xba3516, center: 0xb02a10,
    bloom: "berry", petals: 4, bloomsMin: 6, bloomsMax: 10,
    matureHeight: 110, bushWidth: 70, leafCount: 0, bloomRadius: 9,
    stalkFrac: 0.4, leafShape: "oval",
  },
  gardenia: {
    id: "gardenia", preferredLight: "white", name: "Gardenia", icon: "🤍", form: "shrub", flowering: true,
    seedPrice: 46, sellValue: 122, growSeconds: 14501,
    leaf: 0x2e5b38, leafDark: 0x1b3d22, stem: 0x325a3b, leafAccent: 0x4f8254,
    petal: 0xe2dcc5, petalDark: 0xc4c4b1, center: 0xd3cd9d,
    bloom: "rosette", petals: 14, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 128, bushWidth: 80, leafCount: 0, bloomRadius: 15,
    stalkFrac: 0.4, leafShape: "oval",
  },
  camellia: {
    id: "camellia", preferredLight: "magenta", name: "Camellia", icon: "🌸", form: "shrub", flowering: true,
    seedPrice: 48, sellValue: 126, growSeconds: 16548,
    leaf: 0x295032, leafDark: 0x16331c, stem: 0x3f3126, leafAccent: 0x427146,
    petal: 0xe74a81, petalDark: 0xc23c6c, center: 0xd7a3b6,
    bloom: "rosette", petals: 16, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 138, bushWidth: 82, leafCount: 0, bloomRadius: 16,
    stalkFrac: 0.4, leafShape: "oval",
  },

  // ---- Batch 7: EXOTIC FANTASY FLOWERS (fictional) ----------------------
  // Celestial / cosmic theme
  lumenCactus: {
    id: "lumenCactus", preferredLight: "uv", name: "Lumen Cactus", icon: "🌵", form: "columnar", flowering: true,
    seedPrice: 73, sellValue: 192, growSeconds: 7099,
    leaf: 0x6fb577, leafDark: 0x5b9264, stem: 0x6fb577, leafAccent: 0x8ed1e1,
    petal: 0x86d2df, petalDark: 0x57bff8, center: 0xe6a2e6,
    bloom: "orb", petals: 6, bloomsMin: 5, bloomsMax: 10,
    matureHeight: 128, bushWidth: 56, leafCount: 0, bloomRadius: 9,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 1, padness: 0, spines: true,
  },
  pearlFern: {
    id: "pearlFern", preferredLight: "uv", name: "Pearl Fern", icon: "🐚", form: "fern", flowering: false,
    seedPrice: 72, sellValue: 190, growSeconds: 8551,
    leaf: 0xa1c4b6, leafDark: 0x75b1ba, stem: 0x7bb49d, leafAccent: 0xc49fe6,
    petal: 0xa2bae6, petalDark: 0x9fafd1, center: 0xe6a2d8,
    bloom: "orb", petals: 6, bloomsMin: 0, bloomsMax: 0,
    matureHeight: 120, bushWidth: 92, leafCount: 0, bloomRadius: 10,
    stalkFrac: 0.4, leafShape: "frond", strandCount: 9,
  },
  cometvine: {
    id: "cometvine", preferredLight: "uv", name: "Comet Vine", icon: "☄️", form: "trailing", flowering: true,
    seedPrice: 78, sellValue: 206, growSeconds: 6179,
    leaf: 0x38456d, leafDark: 0x212a47, stem: 0x48557d, leafAccent: 0x718ad2,
    petal: 0xf7a83e, petalDark: 0xce8027, center: 0xe1d18e,
    bloom: "orb", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 110, bushWidth: 72, leafCount: 0, bloomRadius: 12,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 6, trailLength: 92,
  },

  // Crystal / gem theme
  crystalbloom: {
    id: "crystalbloom", preferredLight: "uv", name: "Crystalbloom", icon: "💎", form: "columnar", flowering: true,
    seedPrice: 79, sellValue: 208, growSeconds: 10598,
    leaf: 0x51846d, leafDark: 0x395f50, stem: 0x51846d, leafAccent: 0xa1ccc2,
    petal: 0x7abbdc, petalDark: 0x4494c4, center: 0xa3d6e7,
    bloom: "crystal", petals: 6, bloomsMin: 1, bloomsMax: 3,
    matureHeight: 120, bushWidth: 50, leafCount: 0, bloomRadius: 18,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 3, padness: 0, spines: false,
  },
  amethystThorn: {
    id: "amethystThorn", preferredLight: "magenta", name: "Amethyst Thorn", icon: "🔮", form: "columnar", flowering: true,
    seedPrice: 84, sellValue: 222, growSeconds: 12050,
    leaf: 0x56647f, leafDark: 0x384258, stem: 0x56647f, leafAccent: 0xa793c2,
    petal: 0x9851d6, petalDark: 0x75479d, center: 0xc49fe6,
    bloom: "crystal", petals: 5, bloomsMin: 1, bloomsMax: 2,
    matureHeight: 140, bushWidth: 44, leafCount: 0, bloomRadius: 16,
    stalkFrac: 0.4, leafShape: "needle", stemCount: 4, padness: 0, spines: true,
  },
  prismRose: {
    id: "prismRose", preferredLight: "magenta", name: "Prism Rose", icon: "🌈", form: "bush", flowering: true,
    seedPrice: 85, sellValue: 224, growSeconds: 9678,
    leaf: 0x3a654e, leafDark: 0x223f2f, stem: 0x325a41,
    petal: 0xdc7ab5, petalDark: 0xa84888, center: 0xe7a5e7,
    bloom: "crystal", petals: 6, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 124, bushWidth: 48, leafCount: 16, bloomRadius: 17,
    stalkFrac: 0.5, leafShape: "lobed",
  },

  // Fire / ember theme
  emberbloom: {
    id: "emberbloom", preferredLight: "red", name: "Emberbloom", icon: "🔥", form: "bush", flowering: true,
    seedPrice: 90, sellValue: 238, growSeconds: 9146,
    leaf: 0x7b422a, leafDark: 0x4c2719, stem: 0x6c4129, leafAccent: 0xe37a19,
    petal: 0xdf4816, petalDark: 0xb02e16, center: 0xf6bc28,
    bloom: "pom", petals: 24, bloomsMin: 4, bloomsMax: 6,
    matureHeight: 150, bushWidth: 58, leafCount: 14, bloomRadius: 16,
    stalkFrac: 0.5, leafShape: "lobed",
  },
  phoenixLily: {
    id: "phoenixLily", preferredLight: "red", name: "Phoenix Lily", icon: "🪶", form: "bush", flowering: true,
    seedPrice: 91, sellValue: 240, growSeconds: 10598,
    leaf: 0x6c4129, leafDark: 0x482a1a, stem: 0x7b502a, leafAccent: 0xe59832,
    petal: 0xf56012, petalDark: 0xc74616, center: 0xf7ca3e,
    bloom: "ray", petals: 14, bloomsMin: 1, bloomsMax: 2,
    matureHeight: 156, bushWidth: 46, leafCount: 10, bloomRadius: 24,
    stalkFrac: 0.7, leafShape: "blade",
  },
  magmaSucculent: {
    id: "magmaSucculent", preferredLight: "red", name: "Magma Rosette", icon: "🌋", form: "rosette", flowering: false,
    seedPrice: 96, sellValue: 252, growSeconds: 12050,
    leaf: 0x7b352a, leafDark: 0x4c1f19, stem: 0x6c3429, leafAccent: 0xe37a19,
    petal: 0xdf4816, petalDark: 0xb02e16, center: 0xf6bc28,
    bloom: "star", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 84, bushWidth: 56, leafCount: 24, bloomRadius: 8,
    stalkFrac: 0.7, leafShape: "round", rosetteLeaves: 24,
  },

  // Ice / frost theme
  frostfern: {
    id: "frostfern", preferredLight: "blue", name: "Frostfern", icon: "❄️", form: "fern", flowering: false,
    seedPrice: 97, sellValue: 254, growSeconds: 9678,
    leaf: 0x98b4c3, leafDark: 0x6f98b3, stem: 0x85acc8, leafAccent: 0xa3cae7,
    petal: 0xa3d6e7, petalDark: 0x9fc0d1, center: 0xcbe3d7,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 116, bushWidth: 76, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 8,
  },
  glacierlily: {
    id: "glacierlily", preferredLight: "white", name: "Glacier Lily", icon: "🧊", form: "bush", flowering: true,
    seedPrice: 102, sellValue: 268, growSeconds: 8551,
    leaf: 0x648fa5, leafDark: 0x4d6f83, stem: 0x648fa5, leafAccent: 0xa3c6d7,
    petal: 0x94c6e2, petalDark: 0x6eaddb, center: 0xa3d6e7,
    bloom: "crystal", petals: 6, bloomsMin: 2, bloomsMax: 3,
    matureHeight: 122, bushWidth: 44, leafCount: 10, bloomRadius: 18,
    stalkFrac: 0.66, leafShape: "blade",
  },

  // Fae / enchanted theme
  cloudPuff: {
    id: "cloudPuff", preferredLight: "white", name: "Cloud Puff", icon: "☁️", form: "grass", flowering: true,
    seedPrice: 103, sellValue: 270, growSeconds: 7099,
    leaf: 0x587a62, leafDark: 0x3f5c48, stem: 0x587a62,
    petal: 0xe18ec1, petalDark: 0xcd54aa, center: 0xe69fcd,
    bloom: "orb", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 168, bushWidth: 60, leafCount: 0, bloomRadius: 20,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 16, plume: true,
  },
  willowisp: {
    id: "willowisp", preferredLight: "uv", name: "Will-o'-Wisp Reed", icon: "👻", form: "grass", flowering: true,
    seedPrice: 109, sellValue: 286, growSeconds: 8551,
    leaf: 0x405c4e, leafDark: 0x2a4036, stem: 0x405c4e,
    petal: 0x8cd0a8, petalDark: 0x4e956b, center: 0xa3e7b6,
    bloom: "orb", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 158, bushWidth: 58, leafCount: 0, bloomRadius: 10,
    stalkFrac: 0.5, leafShape: "blade", strandCount: 16, plume: true,
  },
  honeybell: {
    id: "honeybell", preferredLight: "white", name: "Honeybell", icon: "🍯", form: "bush", flowering: true,
    seedPrice: 108, sellValue: 284, growSeconds: 5052,
    leaf: 0x4e7358, leafDark: 0x36543d, stem: 0x4d6850, leafAccent: 0x68ad7b,
    petal: 0xe3d197, petalDark: 0xe4bb5c, center: 0xdfcb86,
    bloom: "bell", petals: 6, bloomsMin: 6, bloomsMax: 12,
    matureHeight: 118, bushWidth: 48, leafCount: 6, bloomRadius: 11,
    stalkFrac: 0.62, leafShape: "blade",
  },
  lanternMoss: {
    id: "lanternMoss", preferredLight: "uv", name: "Lantern Moss", icon: "🟢", form: "shrub", flowering: true,
    seedPrice: 114, sellValue: 300, growSeconds: 7099,
    leaf: 0x4e7353, leafDark: 0x345239, stem: 0x5e9b5c, leafAccent: 0x4fc252,
    petal: 0x8be0c2, petalDark: 0x47a87b, center: 0xa3e7d0,
    bloom: "orb", petals: 5, bloomsMin: 4, bloomsMax: 7,
    matureHeight: 78, bushWidth: 58, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "round",
  },
  glassvine: {
    id: "glassvine", preferredLight: "uv", name: "Glassvine", icon: "🫧", form: "trailing", flowering: true,
    seedPrice: 115, sellValue: 302, growSeconds: 14501,
    leaf: 0x6ebfdb, leafDark: 0x4d9cc4, stem: 0x88bbcd, leafAccent: 0x9cd1e5,
    petal: 0x94c9e2, petalDark: 0x6eaddb, center: 0xa3dde7,
    bloom: "orb", petals: 5, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 100, bushWidth: 72, leafCount: 0, bloomRadius: 9,
    stalkFrac: 0.4, leafShape: "heart", strandCount: 6, trailLength: 92,
  },

  // ── PREMIUM (shop-only, entitlement-granted; NEVER level-unlocked — see level.ts PREMIUM_PLANTS) ──
  //
  // THE `growSecondsOverride: 1200` ON ALL NINE IS DELIBERATE, NOT AN OVERSIGHT. Every other species in the
  // catalogue takes its time from the ladder income curve (`baseGrowSeconds`: 10 coins/min at sellValue 40,
  // rising gently to ≈13.5 at 302). These nine pin 20 minutes against sellValues of 640–1120, a 2.1×–3.4×
  // speed-up over that curve, which puts them at 32–56 GROSS coins/min — 20–36/min net of re-buying the
  // seed, and ≥2.3× the TOP of the ladder. That is the INTENDED number: paid plants earn a premium multiple
  // that sits clear above the ladder curve rather than colliding with its top.
  //
  // WHY IT DOESN'T BREAK BALANCE §8 INVARIANT #1. That invariant governs the LEVEL LADDER: no plant may
  // obsolete one a player levelled to unlock. Premium plants are granted by entitlement and sit outside
  // the unlock ladder (level.ts PREMIUM_PLANT_IDS), so nothing on the ladder is competing with them. The exception is bounded by this
  // block and must stay bounded — a `growSecondsOverride` on a level-gated plant breaks the invariant
  // outright. The `growSeconds: 21600` each still carries is a legacy field nothing reads; were the
  // override removed, `baseGrowSeconds` would put them on the curve at ≈42–68 min.
  //
  // NUMBERS A FUTURE EDITOR MUST NOT CHANGE CASUALLY: the 1200 s override, the 640–1120 sellValue band, and
  // the seedPrice ≈ 0.36–0.38 × sellValue ratio (the only thing keeping net from running away with gross).
  // Moving any of them moves the premium coins/min, which is recorded in BALANCE §1a's table AND sized into
  // the server's per-hour coin plausibility cap (server/index.js, CURRENCY_LIMITS.coins). Retune all three
  // together or an honest premium player starts getting clamped.
  //
  // The VOID ORCHID: a fantasy BLACK orchid whose blooms are near-black with an iridescent violet+teal
  // rim-light and a silver edge, wreathed in a cosmic nebula glow + drifting star sparkles. Bespoke
  // renderer (render/plants/voidOrchid.ts) draws the phalaenopsis blooms + space FX; the `bloom`/`petal`
  // fields below are the fallback/insect-height hints only.
  voidOrchid: {
    id: "voidOrchid", preferredLight: "uv", name: "Void Orchid", icon: "🌌", form: "bush", flowering: true,
    seedPrice: 240, sellValue: 640, growSeconds: 21600, growSecondsOverride: 1200,
    // near-black foliage/petals with a silver stem + luminous teal accent + violet lip/centre.
    leaf: 0x0f121a, leafDark: 0x05070a, stem: 0xa1a9b4, leafAccent: 0x2bd4dc,
    petal: 0x06070b, petalDark: 0x000000, center: 0x824cf8,
    bloom: "cup", petals: 5, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 138, bushWidth: 104, leafCount: 0, bloomRadius: 24,
    stalkFrac: 0.5, leafShape: "blade",
  },
  abyssBlossom: {
    id: "abyssBlossom", preferredLight: "uv", name: "Abyss Blossom", icon: "🌑", form: "bush", flowering: true,
    seedPrice: 400, sellValue: 1120, growSeconds: 21600, growSecondsOverride: 1200,
    // near-black foliage/stem with a gold accent; black→indigo petals (petal) + a gold-violet heart (center).
    leaf: 0x100e1e, leafDark: 0x040307, stem: 0x181441, leafAccent: 0xcc9f36,
    petal: 0x181441, petalDark: 0x040307, center: 0xcc9f36,
    bloom: "cup", petals: 8, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 138, bushWidth: 122, leafCount: 0, bloomRadius: 36,
    stalkFrac: 0.5, leafShape: "blade",
  },
  lightbloom: {
    id: "lightbloom", preferredLight: "white", name: "Lightbloom", icon: "🕊️", form: "bush", flowering: true,
    seedPrice: 380, sellValue: 1060, growSeconds: 21600, growSecondsOverride: 1200,
    // pale/gold foliage + gold stem; radiant white petals (petal) with a warm cream heart (center).
    leaf: 0xc5bda9, leafDark: 0xcb9f42, stem: 0xcb9f42, leafAccent: 0x88c9df,
    petal: 0xd2c9b3, petalDark: 0xc5bda9, center: 0xe0d8c0,
    bloom: "cup", petals: 6, bloomsMin: 1, bloomsMax: 3,
    matureHeight: 138, bushWidth: 128, leafCount: 0, bloomRadius: 28,
    stalkFrac: 0.5, leafShape: "blade",
  },
  bloodshadowRose: {
    id: "bloodshadowRose", preferredLight: "magenta", name: "Bloodshadow Rose", icon: "🥀", form: "bush", flowering: true,
    seedPrice: 360, sellValue: 1000, growSeconds: 21600, growSecondsOverride: 1200,
    // gunmetal foliage/stem; crimson-to-near-black petals (petal) with a glowing red heart (center).
    leaf: 0x1d2227, leafDark: 0x0e0f13, stem: 0x24272b, leafAccent: 0xa39886,
    petal: 0x951934, petalDark: 0x4a0b1b, center: 0xf51235,
    bloom: "cup", petals: 6, bloomsMin: 1, bloomsMax: 3,
    matureHeight: 140, bushWidth: 124, leafCount: 0, bloomRadius: 27,
    stalkFrac: 0.5, leafShape: "blade",
  },
  nightglowMushroom: {
    id: "nightglowMushroom", preferredLight: "blue", name: "Nightglow Mushroom", icon: "🍄", form: "bush", flowering: true,
    seedPrice: 340, sellValue: 940, growSeconds: 21600, growSecondsOverride: 1200,
    // teal leaves + blue/teal stems; deep-purple caps (petal) with luminous teal freckles (center/accent).
    leaf: 0x237051, leafDark: 0x164f37, stem: 0x2f77c7, leafAccent: 0x2ae099,
    petal: 0x331e59, petalDark: 0x1c0f32, center: 0x35f0a2,
    bloom: "cup", petals: 5, bloomsMin: 4, bloomsMax: 4,
    matureHeight: 120, bushWidth: 116, leafCount: 0, bloomRadius: 26,
    stalkFrac: 0.5, leafShape: "blade",
  },
  vampiricPitcher: {
    id: "vampiricPitcher", preferredLight: "magenta", name: "Vampiric Pitcher", icon: "🧛", form: "trailing", flowering: true,
    seedPrice: 320, sellValue: 880, growSeconds: 21600, growSecondsOverride: 1200,
    // teal crown foliage; blood-crimson HANGING pitcher tubes (petal) with a glowing red throat (center).
    leaf: 0x226545, leafDark: 0x154931, stem: 0x4e2e66, leafAccent: 0xf1239c,
    petal: 0x8d173a, petalDark: 0x3a0a1b, center: 0xf51244,
    bloom: "cup", petals: 5, bloomsMin: 3, bloomsMax: 5,
    matureHeight: 120, bushWidth: 116, leafCount: 0, bloomRadius: 20,
    stalkFrac: 0.5, leafShape: "blade", stemCount: 4, trailLength: 96,
  },
  waterDandelion: {
    id: "waterDandelion", preferredLight: "blue", name: "Water Dandelion", icon: "💧", form: "bush", flowering: true,
    seedPrice: 300, sellValue: 820, growSeconds: 21600, growSecondsOverride: 1200,
    // green dandelion foliage/stalk; a translucent blue water puff (petal = water tint, center = deep water).
    leaf: 0x3c7051, leafDark: 0x254f36, stem: 0x4f7759, leafAccent: 0x9ccae5,
    petal: 0x59b0da, petalDark: 0x3377b4, center: 0x1a358c,
    bloom: "orb", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 132, bushWidth: 110, leafCount: 0, bloomRadius: 34,
    stalkFrac: 0.6, leafShape: "blade",
  },
  lilyOfTheDead: {
    id: "lilyOfTheDead", preferredLight: "uv", name: "Lily of the Dead", icon: "💀", form: "bush", flowering: true,
    seedPrice: 280, sellValue: 760, growSeconds: 21600, growSecondsOverride: 1200,
    // dark teal foliage + silvery stem; ghostly white-cyan bells with a violet bud tip + faint gold rim.
    leaf: 0x274037, leafDark: 0x172e2d, stem: 0x7c8d86, leafAccent: 0x8cd0c8,
    petal: 0xa3dde7, petalDark: 0x9fc7d0, center: 0x7b63bb,
    bloom: "bell", petals: 5, bloomsMin: 2, bloomsMax: 4,
    matureHeight: 168, bushWidth: 110, leafCount: 0, bloomRadius: 18,
    stalkFrac: 0.5, leafShape: "blade",
  },
  auroraFern: {
    id: "auroraFern", preferredLight: "uv", name: "Aurora Fern", icon: "🌌", form: "fern", flowering: false,
    seedPrice: 260, sellValue: 700, growSeconds: 21600, growSecondsOverride: 1200,
    // lush fronds that gradient through the aurora — deep indigo base → teal → green → violet tips,
    // luminous cyan accent for the glow/veins. `leaf`/`leafDark` anchor the frond gradient endpoints.
    leaf: 0x328f67, leafDark: 0x1b2c5e, stem: 0x254475, leafAccent: 0x7adcc2,
    petal: 0x39f099, petalDark: 0x3759c1, center: 0xac89e0,
    bloom: "spike", petals: 6, bloomsMin: 1, bloomsMax: 1,
    matureHeight: 132, bushWidth: 108, leafCount: 0, bloomRadius: 6,
    stalkFrac: 0.5, leafShape: "frond", strandCount: 11,
  },
};

/** All species ids, in registry order (drives shop/market ordering + hashing). */
export const FLOWER_IDS: FlowerId[] = Object.keys(FLOWERS);
/** EVERY registered plant id (flowers, succulents, cacti, ferns, grasses, shrubs, …) — used by the host-set
 *  coverage check ("does every plant have a purpose?"). */
export const ALL_PLANT_IDS: FlowerId[] = Object.keys(FLOWERS);

export function flower(id: FlowerId): FlowerSpecies {
  return FLOWERS[id] ?? FLOWERS[FLOWER_IDS[0]!]!;
}

/** The display name for a SEED PACKET of this species — the plant name + " Seeds" (e.g. "Daisy Seeds").
 *  Used wherever a seed (not the grown plant / cut flower) is named: the seed shop + the inventory. */
export function seedName(id: FlowerId): string { return `${flower(id).name} Seeds`; }

/**
 * Where a species' BLOSSOMS sit, as a fraction of `matureHeight` above the plant's BASE — so a butterfly
 * can perch ON the flowers rather than at a fixed height for every plant. Derived from the actual renderer:
 * a bush's blooms ride a stalk (stalkFrac) above the leaf crown (~0.34·H); other forms hold their colour at
 * form-specific heights (rosettes/succulents low, grasses/columnar taller, trailing plants drape a bit
 * below their crown). Clamped to a sane band. Non-flowering foliage plants → their foliage crown height.
 */
export function bloomHeightFrac(sp: FlowerSpecies): number {
  const form = sp.form ?? "bush";
  const stalk = sp.stalkFrac || 0.5;
  let frac: number;
  switch (form) {
    case "bush":     frac = 0.34 + stalk * 0.7; break;   // leaf crown + the flower stalk
    case "shrub":    frac = 0.55 + stalk * 0.35; break;  // dense rounded mound, blooms across it
    case "rosette":  frac = 0.42; break;                 // low fleshy rosette — colour sits near the centre
    case "columnar": frac = 0.7 + stalk * 0.2; break;    // upright stems, flowers near the top
    case "grass":    frac = 0.7; break;                  // plumes ride high on the blades
    case "fern":     frac = 0.55; break;                 // arching fronds — mid height
    case "trailing": frac = 0.3; break;                  // drapes over the rim — blooms low/among the vines
    default:         frac = 0.34 + stalk * 0.7;
  }
  return Math.max(0.28, Math.min(1.05, frac));
}

// ---- GROWTH TIME -----------------------------------------------------------
// Growth time is DERIVED FROM RARITY (a plant's sellValue), so the catalogue scales
// automatically and the design stays a single tunable curve. Cheap commons mature
// in minutes (snappy, session-scale active play); the top of the ladder takes about 22 minutes
// (Glassvine, 1,338 s) and premium blooms pin 20 min via `growSecondsOverride`. The value
// gap is wider than the price gap (power curve), which is what makes self-supplying a
// premium plant costly enough that a future auction house / trade economy matters.
//
// Boosts (grow lights of the right colour, moisture, compost quick-bloom, …) layer ON TOP as a
// SPEED MULTIPLIER, and they are ADDITIVE: `speedMultFor` in sim/plantGrowth.ts sums them onto 1
// rather than multiplying, so a plant with two +30% boosts grows at 1.6×, not 1.69×. There is no
// ceiling in the code — the practical cap is the sum of every term at its best (MAX_SPEED_MULT,
// 2.12, which the server clamps banked progress against). That is THE speed story: at most 2.12×,
// i.e. a plant never takes less than ~0.47 × its base time. GROWTH_SPEED_FLOOR below (0.4 = 2.5×) is
// looser than that, so it never binds; it survives only inside `effectiveGrowSeconds`, which live
// growth no longer uses.

/**
 * GENTLY RISING economy. Coins-per-minute-per-pot climbs slowly with sellValue on ONE power curve:
 *
 *   rate(v) = COINS_PER_MIN × (v / INCOME_CURVE_BASE)^INCOME_CURVE_EXP      coins/min/pot
 *   grow(v) = v / rate(v) × 60 s   (≈ 10.43 × v^0.85 s)
 *
 * The Daisy (40) earns exactly 10/min, the median rung (~110) ≈11.6/min, Glassvine (302) ≈13.5/min —
 * monotonic, no tiers, no cliffs — so every later unlock is a small, real upgrade per pot and which
 * species goes on a shelf matters. It replaced a FLAT rule (grow = v × 6 s, 10/min for every plant) under
 * which ~98 of the 100 ladder plants were strictly dominated and species choice was cosmetic.
 *
 * The exponent is deliberately SMALL: the top plant out-earns a starter by ~35%, not by multiples, so an
 * early plant is behind but never worthless and progression is still carried mostly by bigger absolute
 * payouts, unlocks and XP-rate. Do not steepen it casually — XP/hour ∝ v^(1.25 − 0.85) rides on the same
 * curve (BALANCE §2), and the premium band (32–56/min, BALANCE §1a) must stay well above its top.
 *
 * A species may still pin an explicit time with `growSecondsOverride`; only the premium plants do.
 */
export const COINS_PER_MIN = 10;              // rate at the BOTTOM of the ladder (sellValue 40): 10 coins/min/pot
const INCOME_CURVE_BASE = 40;                 // sellValue at which the rate is exactly COINS_PER_MIN (Daisy)
const INCOME_CURVE_EXP = 0.15;                // 40 → 10/min, 302 → 13.5/min: the whole upward slope lives here
const GROW_MIN_SECONDS = 240;                 // safety floor so nothing is trivially fast (4 min)
// No ceiling (review #105): the old 3600 s one never bound — the ladder tops out at Glassvine, 1,338 s, and
// premium plants pin their time with `growSecondsOverride`. The LEVEL-LADDER catalogue spans sellValue
// 40 (common) ‥ 302 (top-tier fantasy, Glassvine); premium plants (640–1120) sit deliberately OUTSIDE that
// span — off the ladder entirely. See the PREMIUM block above and BALANCE §1a.
/** LEGACY clamp for `effectiveGrowSeconds` only: never less than this FRACTION of base time (2.5×).
 *  It does not bind — the real ceiling is MAX_SPEED_MULT 2.12 (sim/plantGrowth.ts), ≈0.47 × base. */
export const GROWTH_SPEED_FLOOR = 0.4;

/** Coins per minute per pot that a sellValue earns on the ladder income curve (zero boosts, gross). */
export function ladderCoinsPerMin(sellValue: number): number {
  return COINS_PER_MIN * Math.pow(Math.max(1, sellValue / INCOME_CURVE_BASE), INCOME_CURVE_EXP);
}

/** BASE growth seconds for a species (zero boosts): sellValue ÷ its ladder rate, floored at 4 min.
 *  A species may pin an explicit time with `growSecondsOverride` (the premium plants do). */
export function baseGrowSeconds(sp: FlowerSpecies): number {
  if (typeof sp.growSecondsOverride === "number") return sp.growSecondsOverride;
  const onCurve = (sp.sellValue / ladderCoinsPerMin(sp.sellValue)) * 60;
  return Math.max(GROW_MIN_SECONDS, onCurve);
}

/** EFFECTIVE growth seconds = base ÷ speedMultiplier, clamped so it never drops below
 *  base × GROWTH_SPEED_FLOOR. `speedMultiplier` comes from `speedMultFor` (sim/plantGrowth.ts),
 *  which ADDS its boosts onto 1 (damp soil +0.30, quick-bloom compost,
 *  night penalty −0.20) rather than multiplying them.
 *
 *  THIS IS NO LONGER THE LIVE GROWTH PATH. Growing plants BANK progress per tick (`bankProgress`),
 *  so the floor here is not a cap on a real plant's speed (MAX_SPEED_MULT is); this runs only for LEGACY plantings saved
 *  before banking existed, and for the pot dialog's remaining-time readout. */
export function effectiveGrowSeconds(sp: FlowerSpecies, speedMultiplier = 1, allowSlow = false): number {
  const base = baseGrowSeconds(sp);
  // By default boosts only ever speed up (m ≥ 1). `allowSlow` lets a penalty (e.g. DRY soil moisture)
  // push m below 1 to SLOW growth — clamped so it never stalls entirely (min 0.4× speed = 2.5× time).
  const m = allowSlow ? Math.max(0.4, speedMultiplier) : Math.max(1, speedMultiplier);
  return Math.max(base * GROWTH_SPEED_FLOOR, base / m);
}
/** Growth form of a species (defaulting to bush). */
export function formOf(sp: FlowerSpecies): PlantForm {
  return sp.form ?? "bush";
}
/** True if a species is a TRAILING/vine form — these hang over an edge, so in the garden they REQUIRE a
 *  host (planter/basket/urn) rather than being placed on bare ground. */
export function isTrailing(id: FlowerId): boolean {
  return formOf(flower(id)) === "trailing";
}
